import { useEffect, useState } from 'react'
import { Drawer, Tabs, Table, Typography, Button, Tag, Empty, message } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { getCaptureFlow, repeatCaptureFlow } from '@/api/capture'
import type { CaptureFlow, CaptureHeaderItem, CaptureWsMessage } from '@/types'

const { Text, Paragraph } = Typography

const monoFont = "'JetBrains Mono', 'Fira Code', 'Consolas', monospace"

/** 敏感头（展示时遮罩，可复制原文） */
const SENSITIVE_KEYS = ['authorization', 'cookie', 'set-cookie']

const isSensitive = (key: string) => SENSITIVE_KEYS.includes((key || '').toLowerCase())

/** JSON 美化（失败时原文返回） */
const prettyBody = (body: string): string => {
  if (!body) return ''
  try {
    return JSON.stringify(JSON.parse(body), null, 2)
  } catch {
    return body
  }
}

interface Props {
  open: boolean
  flowId: number | null
  onClose: () => void
  onImport: (flow: CaptureFlow) => void
  /** 重发成功后通知父组件刷新列表 */
  onRepeated?: () => void
}

/** 抓包记录详情抽屉：请求 / 响应 / 消息（WS/SSE）/ 原始信息 */
export default function FlowDetailDrawer({ open, flowId, onClose, onImport, onRepeated }: Props) {
  const [detail, setDetail] = useState<CaptureFlow | null>(null)
  const [loading, setLoading] = useState(false)
  const [repeating, setRepeating] = useState(false)

  useEffect(() => {
    if (!open || !flowId) return
    setLoading(true)
    getCaptureFlow(flowId)
      .then(setDetail)
      .finally(() => setLoading(false))
  }, [open, flowId])

  const copyText = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      message.success('已复制原文')
    } catch {
      message.error('复制失败')
    }
  }

  /** 原样重发：后端 httpx 重放，结果作为新记录入库（is_replay 标记） */
  const handleRepeat = async () => {
    if (!detail) return
    setRepeating(true)
    try {
      const res = await repeatCaptureFlow(detail.id)
      if (res.error_msg) {
        message.warning(`重发失败：${res.error_msg}（已记录）`)
      } else {
        message.success(`重发完成：${res.status_code} · ${res.duration_ms} ms，已生成新记录`)
      }
      onRepeated?.()
    } catch {
      /* 拦截器已提示 */
    } finally {
      setRepeating(false)
    }
  }

  const headerColumns: ColumnsType<CaptureHeaderItem> = [
    { title: 'Header', dataIndex: 'key', width: 180, render: (v: string) => <Text code style={{ fontFamily: monoFont }}>{v}</Text> },
    {
      title: 'Value',
      dataIndex: 'value',
      render: (v: string, record: CaptureHeaderItem) =>
        isSensitive(record.key) ? (
          <span>
            <Text type="secondary">****（已遮罩）</Text>{' '}
            <Button type="link" size="small" onClick={() => copyText(v)} style={{ padding: 0 }}>
              复制原文
            </Button>
          </span>
        ) : (
          <span style={{ fontFamily: monoFont, wordBreak: 'break-all' }}>{v}</span>
        ),
    },
  ]

  /** WS/SSE 消息列表 */
  const messageColumns: ColumnsType<CaptureWsMessage> = [
    {
      title: '方向',
      dataIndex: 'direction',
      width: 90,
      align: 'center',
      render: (v: CaptureWsMessage['direction']) =>
        v === 'sent' ? (
          <Tag color="processing">↑ 发送</Tag>
        ) : v === 'received' ? (
          <Tag color="success">↓ 接收</Tag>
        ) : (
          <Tag>提示</Tag>
        ),
    },
    {
      title: '偏移',
      dataIndex: 'time_offset_ms',
      width: 90,
      align: 'right',
      render: (v: number) => <span style={{ fontFamily: monoFont, fontSize: 12 }}>{v} ms</span>,
    },
    {
      title: '内容',
      dataIndex: 'text',
      render: (v: string, r: CaptureWsMessage) =>
        r.opcode === 2 ? (
          <Text type="secondary" style={{ fontSize: 12 }}>{v}</Text>
        ) : (
          <pre
            style={{
              margin: 0,
              padding: 8,
              maxHeight: 200,
              overflow: 'auto',
              background: 'var(--au-primary-50, #f6f8f5)',
              border: '1px solid var(--au-border, #e4e2dc)',
              borderRadius: 6,
              fontFamily: monoFont,
              fontSize: 12,
              lineHeight: 1.6,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
            }}
          >
            {v}
          </pre>
        ),
    },
  ]

  const renderBody = (body: string, contentType: string) => {
    if (!body) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="无请求体" />
    return (
      <div>
        {contentType && (
          <div style={{ marginBottom: 8 }}>
            <Tag>{contentType}</Tag>
          </div>
        )}
        <pre
          style={{
            margin: 0,
            padding: 12,
            maxHeight: 360,
            overflow: 'auto',
            background: 'var(--au-primary-50, #f6f8f5)',
            border: '1px solid var(--au-border, #e4e2dc)',
            borderRadius: 8,
            fontFamily: monoFont,
            fontSize: 12,
            lineHeight: 1.6,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
          }}
        >
          {prettyBody(body)}
        </pre>
      </div>
    )
  }

  const fullUrl = detail ? `${detail.scheme}://${detail.host}${detail.port && detail.port !== 80 && detail.port !== 443 ? `:${detail.port}` : ''}${detail.path}${detail.query ? `?${detail.query}` : ''}` : ''
  const wsMessages = detail?.ws_messages || []

  return (
    <Drawer
      title="抓包详情"
      width={680}
      open={open}
      onClose={onClose}
      destroyOnClose
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={onClose}>关闭</Button>
          <Button
            style={{ borderRadius: 999 }}
            disabled={!detail}
            loading={repeating}
            onClick={handleRepeat}
          >
            重发
          </Button>
          <Button
            type="primary"
            disabled={!detail}
            onClick={() => detail && onImport(detail)}
          >
            导入此接口
          </Button>
        </div>
      }
    >
      {detail && (
        <>
          <div style={{ marginBottom: 12, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <Tag color="blue" style={{ fontWeight: 700 }}>{detail.method}</Tag>
            <Tag color={detail.status_code >= 500 ? 'error' : detail.status_code >= 400 ? 'warning' : detail.status_code >= 300 ? 'processing' : detail.status_code > 0 ? 'success' : 'default'}>
              {detail.status_code > 0 ? detail.status_code : '无响应'}
            </Tag>
            {detail.is_replay && <Tag color="purple">重发</Tag>}
            <Text type="secondary" style={{ fontFamily: monoFont, fontSize: 12 }}>{detail.duration_ms} ms</Text>
          </div>
          <Paragraph copyable style={{ fontFamily: monoFont, fontSize: 12, wordBreak: 'break-all' }}>
            {fullUrl}
          </Paragraph>
          {detail.error_msg && (
            <Paragraph type="danger" style={{ fontSize: 12 }}>
              错误：{detail.error_msg}
            </Paragraph>
          )}
        </>
      )}

      <Tabs
        defaultActiveKey="request"
        items={[
          {
            key: 'request',
            label: '请求',
            children: detail ? (
              <>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>请求头</Text>
                <Table<CaptureHeaderItem>
                  rowKey={(r, i) => `h-${i}`}
                  size="small"
                  pagination={false}
                  columns={headerColumns}
                  dataSource={detail.req_headers || []}
                  locale={{ emptyText: '无请求头' }}
                  style={{ marginBottom: 16 }}
                />
                <Text strong style={{ display: 'block', marginBottom: 8 }}>请求体</Text>
                {renderBody(detail.req_body || '', '')}
              </>
            ) : null,
          },
          {
            key: 'response',
            label: '响应',
            children: detail ? (
              <>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>响应头</Text>
                <Table<CaptureHeaderItem>
                  rowKey={(r, i) => `rh-${i}`}
                  size="small"
                  pagination={false}
                  columns={headerColumns}
                  dataSource={detail.resp_headers || []}
                  locale={{ emptyText: '无响应头' }}
                  style={{ marginBottom: 16 }}
                />
                <Text strong style={{ display: 'block', marginBottom: 8 }}>响应体</Text>
                {renderBody(detail.resp_body || '', detail.content_type)}
              </>
            ) : null,
          },
          {
            key: 'messages',
            label: `消息${wsMessages.length ? ` (${wsMessages.length})` : ''}`,
            children: wsMessages.length ? (
              <Table<CaptureWsMessage>
                rowKey={(_, i) => `m-${i}`}
                size="small"
                pagination={false}
                columns={messageColumns}
                dataSource={wsMessages}
              />
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="无 WebSocket / SSE 消息（仅 WS 通讯与 SSE 流式响应记录于此）" />
            ),
          },
          {
            key: 'raw',
            label: '原始信息',
            children: detail ? (
              <Table
                rowKey={(r) => (r as { k: string }).k}
                size="small"
                pagination={false}
                showHeader={false}
                dataSource={[
                  { k: 'URL', v: fullUrl },
                  { k: '客户端地址', v: detail.client_addr || '-' },
                  { k: '开始时间', v: detail.start_time || '-' },
                  { k: '耗时', v: `${detail.duration_ms} ms` },
                  { k: '响应 Content-Type', v: detail.content_type || '-' },
                  { k: '重发来源', v: detail.is_replay && detail.replay_source_id ? `#${detail.replay_source_id}` : '原始记录' },
                  { k: '记录时间', v: detail.create_time || '-' },
                ]}
                columns={[
                  { title: 'k', dataIndex: 'k', width: 140, render: (v: string) => <Text strong>{v}</Text> },
                  { title: 'v', dataIndex: 'v', render: (v: string) => <span style={{ fontFamily: monoFont, fontSize: 12, wordBreak: 'break-all' }}>{v}</span> },
                ]}
              />
            ) : null,
          },
        ]}
      />
      {loading && <Text type="secondary">加载中...</Text>}
    </Drawer>
  )
}
